import {
  Component, OnInit, inject, signal, computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { AnnouncementsService } from '../../core/services/announcements.service';
import { Announcement } from '../../core/models/api.models';

const TYPE_COLORS: Record<string, string> = {
  release_note: 'var(--nexus-cyan)',
  announcement: 'var(--nexus-purple)',
  maintenance: '#ffd600',
  incident: '#ff3366',
};

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-whats-new-panel',
  standalone: true,
  imports: [CommonModule, DatePipe],
  templateUrl: './whats-new-panel.component.html',
})
export class WhatsNewPanelComponent implements OnInit {
  private readonly svc = inject(AnnouncementsService);
  protected readonly router = inject(Router);

  protected readonly items = signal<Announcement[]>([]);
  private readonly recencyDays = signal(5);

  protected readonly unreadItems = computed(() => this.items().filter(a => !a.is_read));

  protected readonly recentlyRead = computed(() => {
    const cutoffMs = this.recencyDays() * 24 * 60 * 60 * 1000;
    const cutoff = Date.now() - cutoffMs;
    return this.items().filter(a =>
      a.is_read && new Date(a.published_at ?? a.created_at).getTime() >= cutoff
    );
  });

  ngOnInit(): void {
    this.svc.listPublished().subscribe({
      next: (r) => {
        this.items.set(r.announcements);
        if (r.recency_days > 0) {
          this.recencyDays.set(r.recency_days);
        }
      },
    });
  }

  protected markRead(item: Announcement): void {
    if (item.is_read) return;
    this.svc.markRead(item.id).subscribe({
      next: () => {
        this.items.update(items =>
          items.map(a => a.id === item.id ? { ...a, is_read: true } : a)
        );
        this.svc.loadUnreadCount();
      },
    });
  }

  protected markAllRead(): void {
    this.svc.markAllRead().subscribe({
      next: () => {
        this.items.update(items => items.map(a => ({ ...a, is_read: true })));
        this.svc.loadUnreadCount();
      },
    });
  }

  protected viewAll(): void {
    this.router.navigate(['/whats-new']);
  }

  protected typeClass(type: string): string {
    return 'nx-type-' + (type in TYPE_COLORS ? type : 'unknown');
  }

  protected typeLabel(type: string): string {
    return type.replaceAll('_', ' ');
  }
}
